import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';

import { getMergeRunStatusApi, executeMergeGroupsApi, reloadListingsAfterChange } from 'src/redux/slices/listing';

import { countMergeResults, deriveMergeRunStatus } from '../utils/run-status';
import {
  saveMergeBatchSnapshot,
  readMergeBatchSnapshot,
  pruneMergeBatchSnapshots,
} from '../utils/merge-batch-snapshot';

/**
 * Owns the one merge run the user has going: it polls the batch on the server and hands the
 * result to whoever wants to render it — the floating widget, and the batch progress page.
 *
 * There is deliberately one of these for the whole app, so the run the seller started survives
 * navigating away from it, and a reload picks the same run back up instead of losing track of it.
 *
 * Merging is pure server work — nothing here drives the Chrome extension and nothing here asks
 * the seller to keep the tab open. Once a batch is queued, closing the tab loses nothing: the
 * server keeps merging, and reopening the app resumes watching it.
 */

const POLL_INTERVAL_MS = 2000;

// Once the batch reaches one of these, the server has nothing left to tell us.
const TERMINAL_SERVER_STATUSES = ['completed', 'not_found'];

// The run the user has open survives a reload, but not indefinitely: a day-old run is history,
// not something to reopen a progress widget for.
const ACTIVE_RUN_STORAGE_KEY = 'merge_active_run';
const ACTIVE_RUN_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function readStoredRun() {
  try {
    const raw = localStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw);
    if (!stored?.batchId) return null;

    const submittedAt = new Date(stored.submittedAt).getTime();
    if (!submittedAt || Date.now() - submittedAt > ACTIVE_RUN_MAX_AGE_MS) return null;

    return stored;
  } catch (e) {
    return null;
  }
}

function writeStoredRun(run) {
  try {
    if (run) localStorage.setItem(ACTIVE_RUN_STORAGE_KEY, JSON.stringify(run));
    else localStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
  } catch (e) {
    // A run that cannot be remembered still works for as long as this page is open.
  }
}

/** The last thing this browser knew about a run, or nothing when it has no record of it. */
function storedStatusFor(batchId) {
  return (batchId && readMergeBatchSnapshot(batchId)?.serverStatus) || null;
}

const MergeRunContext = createContext(null);

export function useMergeRun() {
  const context = useContext(MergeRunContext);
  if (!context) {
    throw new Error('useMergeRun must be used within a MergeRunProvider');
  }
  return context;
}

export function MergeRunProvider({ children }) {
  const dispatch = useDispatch();

  const [activeRun, setActiveRun] = useState(readStoredRun);
  // A run this browser already watched finish reads as finished from the very first frame. Without
  // this, reopening the app on a run that is long over shows "Starting…" until the first poll lands.
  const [serverStatus, setServerStatus] = useState(() => storedStatusFor(readStoredRun()?.batchId));
  // Where what we are showing came from: the server right now, the last snapshot we kept of
  // this batch, or nothing at all because both are gone.
  const [source, setSource] = useState('live');

  const batchId = activeRun?.batchId || null;

  // Read by the poll timer and its callbacks, which must not be rebuilt every time these change.
  const serverStatusRef = useRef(serverStatus);
  const activeRunRef = useRef(activeRun);
  useEffect(() => { serverStatusRef.current = serverStatus; }, [serverStatus]);
  useEffect(() => { activeRunRef.current = activeRun; }, [activeRun]);

  // De-dupes the snapshot effect below against re-saving the same status twice in a row.
  const lastSnapshotSignatureRef = useRef(null);

  // ── Keeping the run ───────────────────────────────────────────────────────────────────────

  useEffect(() => {
    pruneMergeBatchSnapshots();
  }, []);

  useEffect(() => {
    writeStoredRun(activeRun);
  }, [activeRun]);

  // Start every batch from what is known about THAT batch — nothing at all for a run just
  // submitted. This provider stays mounted for the life of the app and only the batch it points at
  // changes, so without this each run would inherit the last one's status while its own first poll
  // is still in flight.
  useEffect(() => {
    setServerStatus(storedStatusFor(batchId));
    setSource('live');
    lastSnapshotSignatureRef.current = null;
  }, [batchId]);

  // ── Polling ───────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!batchId) return undefined;

    let stopped = false;
    let interval;

    const reportCompletion = (payload) => {
      dispatch(reloadListingsAfterChange());

      const rowCounts = countMergeResults(payload.results);
      trackEvent(ANALYTICS_EVENTS.MERGE_BATCH_COMPLETED, {
        merged: rowCounts.succeeded,
        failed: rowCounts.failed,
      });
      (payload.results || [])
        .filter((row) => row && !row.success)
        .forEach((row) => {
          trackEvent(ANALYTICS_EVENTS.MERGE_GROUP_FAILED, { errorCode: row.errorCode });
        });

      // The stored latch is what tells a run that has always read 'completed' — because the
      // seller reloaded a page showing a run that finished before they ever saw it — apart from
      // one that has just finished for the first time. It lives on the stored run itself, not a
      // variable in this component, so a reload cannot forget that this run already reported in.
      setActiveRun((run) => (run ? { ...run, completionReported: true } : run));
    };

    const applyPayload = (payload) => {
      if (stopped) return;

      if (payload?.status !== 'not_found') {
        setServerStatus(payload);
        setSource('live');

        if (payload?.status === 'completed' && !activeRunRef.current?.completionReported) {
          reportCompletion(payload);
        }
        return;
      }

      // The server has forgotten this batch, so there is nothing left to ask it for. Show the
      // last snapshot we kept of the run, if we have one.
      clearInterval(interval);

      const snapshot = readMergeBatchSnapshot(batchId);
      if (snapshot?.serverStatus) {
        setServerStatus(snapshot.serverStatus);
        setSource('snapshot');
        return;
      }

      setServerStatus(payload);
      setSource('expired');
    };

    const fetchStatus = async () => {
      try {
        const payload = await getMergeRunStatusApi(batchId);
        applyPayload(payload);
      } catch (err) {
        // A poll that fails leaves the last known status on screen — the next tick tries again.
        console.error('Failed to fetch merge batch status:', err);
      }
    };

    fetchStatus();

    interval = setInterval(() => {
      if (!TERMINAL_SERVER_STATUSES.includes(serverStatusRef.current?.status)) {
        fetchStatus();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [batchId, dispatch]);

  // ── Snapshot, so a finished run survives the server forgetting it ─────────────────────────

  useEffect(() => {
    if (!batchId || !serverStatus || source !== 'live') return;

    const signature = [
      serverStatus.status,
      serverStatus.completed,
      serverStatus.failed,
      serverStatus.progress,
      (serverStatus.results || []).length,
    ].join('|');

    if (signature === lastSnapshotSignatureRef.current) return;
    lastSnapshotSignatureRef.current = signature;

    saveMergeBatchSnapshot(batchId, { startedAt: serverStatus.startedAt, serverStatus });
  }, [batchId, serverStatus, source]);

  // ── Derived view of the run ───────────────────────────────────────────────────────────────

  const counts = useMemo(() => countMergeResults(serverStatus?.results), [serverStatus?.results]);

  const overallStatus = useMemo(
    () => deriveMergeRunStatus(serverStatus, source),
    [serverStatus, source]
  );

  const startedAt = serverStatus?.startedAt || activeRun?.submittedAt || null;

  const isRunFinished = overallStatus === 'completed' || overallStatus === 'completed_with_errors';

  // ── Controls ──────────────────────────────────────────────────────────────────────────────

  const startRun = useCallback((newBatchId, meta = {}) => {
    if (!newBatchId) return;
    setActiveRun({
      batchId: newBatchId,
      submittedAt: Date.now(),
      groupCount: meta.groupCount ?? null,
      listingCount: meta.listingCount ?? null,
      rejectedGroups: meta.rejectedGroups ?? [],
      completionReported: false,
    });
  }, []);

  const dismissRun = useCallback(() => {
    setActiveRun(null);
  }, []);

  // Re-submits one group on its own — the retry a seller reaches for after a group failed to
  // merge. It becomes its own one-group run: the caller gets the new batch id back and decides
  // where to send the seller, because this context only ever tracks a run, never navigates.
  const retryGroup = useCallback(async (group) => {
    try {
      const response = await executeMergeGroupsApi([group]);
      const newBatchId = response?.batchId;

      startRun(newBatchId, {
        groupCount: 1,
        listingCount: 1 + (group?.secondaryListingIds?.length || 0),
        rejectedGroups: [],
      });

      return newBatchId;
    } catch (err) {
      console.error('Failed to retry merge group:', err);
      throw err;
    }
  }, [startRun]);

  /**
   * Look up a batch that is not the active run — for the batch page showing an earlier run.
   * Read-only and silent: a batch nobody is following is never polled again after this, and
   * looking one up is not a run finishing, so it never refreshes listings or reports analytics.
   *
   * Fresh data from the server wins; a saved snapshot stands in once the server has forgotten the
   * batch; if neither is left, the run has expired.
   */
  const inspectBatch = useCallback(async (wantedBatchId) => {
    // `total` is the run's whole group count, set once when the batch was created — distinct
    // from `results.length`, which only ever holds groups that have already settled. A page
    // showing "3 of 5 groups" while the run is still going needs this number, not that one.
    if (!wantedBatchId) {
      return {
        source: 'expired',
        status: deriveMergeRunStatus(null, 'expired'),
        results: [],
        startedAt: null,
        total: 0,
      };
    }

    let liveStatus = null;
    try {
      const payload = await getMergeRunStatusApi(wantedBatchId);
      if (payload?.status !== 'not_found') liveStatus = payload;
    } catch (err) {
      console.error('Failed to look up merge batch:', err);
    }

    if (liveStatus) {
      return {
        source: 'server',
        status: deriveMergeRunStatus(liveStatus, 'live'),
        results: liveStatus.results || [],
        startedAt: liveStatus.startedAt || null,
        total: liveStatus.total || 0,
      };
    }

    const snapshot = readMergeBatchSnapshot(wantedBatchId);
    if (snapshot?.serverStatus) {
      return {
        source: 'snapshot',
        status: deriveMergeRunStatus(snapshot.serverStatus, 'snapshot'),
        results: snapshot.serverStatus.results || [],
        startedAt: snapshot.startedAt || snapshot.serverStatus.startedAt || null,
        total: snapshot.serverStatus.total || 0,
      };
    }

    return {
      source: 'expired',
      status: deriveMergeRunStatus(null, 'expired'),
      results: [],
      startedAt: null,
      total: 0,
    };
  }, []);

  const value = useMemo(() => ({
    activeRun,
    serverStatus,
    source,
    counts,
    overallStatus,
    startedAt,
    isRunFinished,
    startRun,
    dismissRun,
    retryGroup,
    inspectBatch,
  }), [
    activeRun,
    serverStatus,
    source,
    counts,
    overallStatus,
    startedAt,
    isRunFinished,
    startRun,
    dismissRun,
    retryGroup,
    inspectBatch,
  ]);

  return <MergeRunContext.Provider value={value}>{children}</MergeRunContext.Provider>;
}

MergeRunProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
